import { DestroyRef, effect, EffectRef, inject, Injectable, Injector, OnDestroy, runInInjectionContext } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { NavigationEnd, Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { filter, Subject, takeUntil } from 'rxjs';
import { FloFabComponent } from '../views/private/flo/flo-fab.component';
import { FloPanelComponent } from '../views/private/flo/flo-panel.component';
import { FloDisclaimerDialogComponent } from '../views/private/flo/flo-disclaimer-dialog.component';
import { FloChatService } from './flo-chat.service';

/**
 * URL prefix (query/fragment/trailing-slash ignored) where Flô is available: the whole Módulo Flow —
 * Resultados (`/painel/flow-nr1`) and its sub-tabs Estatísticas (`/charts`), Questionário
 * (`/questions`) and the question detail. Flô persists across the report experience instead of
 * vanishing when the user switches tabs. To scope her wider/narrower, change this prefix.
 */
const FLO_ROUTE_PREFIX = '/painel/flow-nr1';

function isFloUrl(url: string): boolean {
	const path = url.split(/[?#]/)[0].replace(/\/+$/, '');
	return path === FLO_ROUTE_PREFIX || path.startsWith(FLO_ROUTE_PREFIX + '/');
}

@Injectable({ providedIn: 'root' })
export class FloOverlayService implements OnDestroy {
	private fabRef: OverlayRef | null = null;
	private panelRef: OverlayRef | null = null;
	private disclaimerDialogRef: MatDialogRef<FloDisclaimerDialogComponent> | null = null;
	private disclaimerPending = false;
	private initialized = false;
	private panelEffectRef: EffectRef | null = null;
	/** Completes on both manual destroy() and Angular DI teardown. */
	private readonly destroyed$ = new Subject<void>();

	constructor(
		private overlay: Overlay,
		private injector: Injector,
		private router: Router,
		private flo: FloChatService,
		private dialog: MatDialog
	) {
		inject(DestroyRef).onDestroy(() => this.destroyed$.next());
	}

	/** Call once from AppComponent.ngOnInit. Idempotent. */
	init(): void {
		if (this.initialized) return;
		this.initialized = true;

		// Signal effect reacts to expanded() changes and attaches/detaches the panel.
		// Covers reactive opens that don't go through the FAB (e.g. expand(),
		// which sets expanded without calling showPanel); the FAB click attaches directly
		// via showPanel() for a synchronous result.
		this.panelEffectRef = runInInjectionContext(this.injector, () =>
			effect(() => {
				const open = this.flo.expanded();
				const onFloRoute = isFloUrl(this.router.url);
				if (open && onFloRoute) {
					this.attachPanel();
				} else {
					this.detachPanel();
				}
			})
		);

		this.router.events
			.pipe(
				filter((e): e is NavigationEnd => e instanceof NavigationEnd),
				takeUntil(this.destroyed$)
			)
			.subscribe(e => this.onUrl(e.urlAfterRedirects));

		// Check current URL at init time (in case already on a Flô route)
		this.onUrl(this.router.url);
	}

	private onUrl(url: string): void {
		const active = isFloUrl(url);
		if (active) {
			this.attachFab();
		} else {
			this.detachAll();
		}
	}

	/** Called directly by FloFabComponent for immediate (synchronous) panel attachment. */
	showPanel(): void {
		if (isFloUrl(this.router.url)) {
			this.attachPanel();
		}
	}

	private attachFab(): void {
		if (this.fabRef) return;
		this.fabRef = this.overlay.create();
		const ref = this.fabRef.attach(new ComponentPortal(FloFabComponent, null, this.injector));
		ref.changeDetectorRef.detectChanges();
	}

	/** Entry gate for showing the panel. Attaches immediately for a user who has acknowledged the
	 *  disclaimer; otherwise resolves the (cached) status and either attaches or presents the required
	 *  first-use disclaimer, attaching only once the user accepts. Covers every open path (FAB click,
	 *  reactive expand) because both funnel here. */
	private attachPanel(): void {
		if (this.panelRef || this.disclaimerDialogRef || this.disclaimerPending) return;
		if (this.flo.disclaimerAcknowledged() === true) {
			this.doAttachPanel();
			return;
		}
		void this.gateDisclaimerThenAttach();
	}

	private async gateDisclaimerThenAttach(): Promise<void> {
		this.disclaimerPending = true;
		try {
			const acknowledged = await this.flo.ensureDisclaimerStatus();
			// The user may have collapsed the chat or navigated away while the status resolved.
			if (this.panelRef || !this.flo.expanded() || !isFloUrl(this.router.url)) return;
			if (acknowledged) { this.doAttachPanel(); return; }
			this.openDisclaimerDialog();
		} finally {
			this.disclaimerPending = false;
		}
	}

	private openDisclaimerDialog(): void {
		this.disclaimerDialogRef = this.dialog.open(FloDisclaimerDialogComponent, {
			disableClose: true,
			autoFocus: false,
			width: '720px',
			maxWidth: '92vw', // responsive: cap at viewport width on small screens
			panelClass: 'flo-disclaimer-dialog'
		});
		this.disclaimerDialogRef.afterClosed().subscribe(accepted => {
			this.disclaimerDialogRef = null;
			if (accepted && this.flo.expanded() && isFloUrl(this.router.url)) {
				this.flo.acknowledgeDisclaimer();
				this.doAttachPanel();
			} else if (!accepted) {
				this.flo.collapse(); // required modal — stay consistent if it ever closes without accepting
			}
		});
	}

	private doAttachPanel(): void {
		if (this.panelRef) return;
		this.panelRef = this.overlay.create();
		const ref = this.panelRef.attach(new ComponentPortal(FloPanelComponent, null, this.injector));
		ref.changeDetectorRef.detectChanges();
	}

	private detachPanel(): void {
		this.panelRef?.dispose();
		this.panelRef = null;
	}

	private detachAll(): void {
		this.fabRef?.dispose();
		this.fabRef = null;
		this.detachPanel();
		this.disclaimerDialogRef?.close();
		this.disclaimerDialogRef = null;
	}

	destroy(): void {
		// Signal the Subject so takeUntil completes the router subscription immediately,
		// regardless of whether Angular DI has torn the service down yet.
		this.destroyed$.next();
		this.destroyed$.complete();
		this.panelEffectRef?.destroy();
		this.panelEffectRef = null;
		this.detachAll();
	}

	ngOnDestroy(): void {
		this.destroy();
	}
}
